import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    InputAdornment,
    IconButton,
    Typography,
    Divider
} from '@mui/material';
import { QrCode as IconBarcode, AutoAwesome as IconMagic } from '@mui/icons-material';
import CodigoBarras from '../common/CodigoBarras';

const ArticuloDialog = ({ open, onClose, onSave, articulo, categorias = [] }) => {
    const [formData, setFormData] = useState({
        id: 0,
        codigo: '',
        codigoBarras: '',
        nombre: '',
        descripcion: '',
        idCategoria: '',
        unidadMedida: 'NIU',
        stockMinimo: 1,
        costoUnitarioPromedio: 0,
        ubicacionAlmacen: '',
        activo: true
    });

    useEffect(() => {
        if (articulo) {
            setFormData({
                id: articulo.id || 0,
                codigo: articulo.codigo || '',
                codigoBarras: articulo.codigoBarras || '',
                nombre: articulo.nombre || '',
                descripcion: articulo.descripcion || '',
                idCategoria: articulo.idCategoria || (categorias[0]?.id || ''),
                unidadMedida: articulo.unidadMedida || 'NIU',
                stockMinimo: articulo.stockMinimo != null ? articulo.stockMinimo : 1,
                costoUnitarioPromedio: articulo.costoUnitarioPromedio != null ? articulo.costoUnitarioPromedio : 0,
                ubicacionAlmacen: articulo.ubicacionAlmacen || '',
                activo: articulo.activo !== false
            });
        } else {
            setFormData({
                id: 0,
                codigo: '',
                codigoBarras: `775${Date.now() % 1000000000}`,
                nombre: '',
                descripcion: '',
                idCategoria: categorias[0]?.id || '',
                unidadMedida: 'NIU',
                stockMinimo: 1,
                costoUnitarioPromedio: 0,
                ubicacionAlmacen: '',
                activo: true
            });
        }
    }, [articulo, categorias, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleGenerarBarcode = () => {
        const nuevo = `775${Math.floor(100000000 + Math.random() * 900000000)}`;
        setFormData((prev) => ({ ...prev, codigoBarras: nuevo }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.nombre.trim()) return;
        onSave(formData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold', color: '#1f2937' }}>
                {formData.id > 0 ? 'Editar Artículo de Inventario' : 'Nuevo Artículo de Material / Inventario'}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Código Interno"
                                name="codigo"
                                value={formData.codigo}
                                onChange={handleChange}
                                placeholder="Ej: MAT-001 (auto si se deja vacío)"
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Código de Barras (EAN / Barcode)"
                                name="codigoBarras"
                                value={formData.codigoBarras}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleGenerarBarcode} size="small" title="Generar código aleatorio">
                                                <IconMagic fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Nombre del Artículo / Insumo"
                                name="nombre"
                                value={formData.nombre}
                                onChange={handleChange}
                                required
                                fullWidth
                                size="small"
                                placeholder="Ej: Pelotas sensoriales con púas"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Descripción / Especificaciones"
                                name="descripcion"
                                value={formData.descripcion}
                                onChange={handleChange}
                                multiline
                                rows={2}
                                fullWidth
                                size="small"
                                placeholder="Detalles de textura, marca o aplicación clínica..."
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Categoría / Grupo</InputLabel>
                                <Select
                                    name="idCategoria"
                                    value={formData.idCategoria}
                                    onChange={handleChange}
                                    label="Categoría / Grupo"
                                >
                                    {categorias.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.descripcion}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Unidad Medida</InputLabel>
                                <Select
                                    name="unidadMedida"
                                    value={formData.unidadMedida}
                                    onChange={handleChange}
                                    label="Unidad Medida"
                                >
                                    <MenuItem value="NIU">Unidad (NIU)</MenuItem>
                                    <MenuItem value="SET">Juego / Set (SET)</MenuItem>
                                    <MenuItem value="PK">Paquete (PK)</MenuItem>
                                    <MenuItem value="BX">Caja (BX)</MenuItem>
                                    <MenuItem value="PZA">Pieza (PZA)</MenuItem>
                                    <MenuItem value="ROL">Rollo (ROL)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Stock Mínimo"
                                name="stockMinimo"
                                type="number"
                                inputProps={{ min: 0, step: 1 }}
                                value={formData.stockMinimo}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Costo Unitario Ref. (S/)"
                                name="costoUnitarioPromedio"
                                type="number"
                                inputProps={{ min: 0, step: '0.01' }}
                                value={formData.costoUnitarioPromedio}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Ubicación en Almacén / Salón"
                                name="ubicacionAlmacen"
                                value={formData.ubicacionAlmacen}
                                onChange={handleChange}
                                placeholder="Ej: Estante B - Salón Sensorial"
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        {/* Vista previa del código de barras */}
                        {formData.codigoBarras && (
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#f9fafb', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        Vista Previa del Código de Barras (Escaneable)
                                    </Typography>
                                    <CodigoBarras valor={formData.codigoBarras} alto={45} />
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} color="inherit">
                        Cancelar
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        Guardar Artículo
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ArticuloDialog;
